import { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import './index.css';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const FinanceAnalytics = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch bookings from Firestore
  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const bookingsCollection = collection(db, 'bookings');
        const bookingsSnapshot = await getDocs(bookingsCollection);
        const bookingsData = bookingsSnapshot.docs.map(doc => {
          const data = doc.data();
          // Safely convert Firestore Timestamps to Date objects
          const checkIn = data.checkIn && data.checkIn.toDate ? data.checkIn.toDate() : null;
          const checkOut = data.checkOut && data.checkOut.toDate ? data.checkOut.toDate() : null;
          const createdAt = data.createdAt && data.createdAt.toDate ? data.createdAt.toDate() : null;
          const paymentDate = data.paymentDate && data.paymentDate.toDate ? data.paymentDate.toDate() : null;

          return {
            id: doc.id,
            ...data,
            checkIn,
            checkOut,
            createdAt,
            paymentDate,
            totalPrice: Number(data.totalPrice) || 0, // Ensure totalPrice is a number
          };
        });

        // Filter for confirmed bookings first
        const confirmedBookings = bookingsData.filter(booking => {
          const isConfirmed = booking.status === 'confirmed';
          if (!isConfirmed) {
            console.warn('Non-confirmed booking skipped:', booking);
          }
          return isConfirmed;
        });

        // Further filter for valid confirmed bookings
        const validBookings = confirmedBookings.filter(booking => {
          const isValid =
            booking.checkIn instanceof Date &&
            !isNaN(booking.checkIn) &&
            typeof booking.totalPrice === 'number' &&
            !isNaN(booking.totalPrice);
          if (!isValid) {
            console.warn('Invalid confirmed booking:', booking);
          }
          return isValid;
        });

        console.log('Valid confirmed bookings:', validBookings); 
        setBookings(validBookings);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching bookings:', error);
        setError('Failed to load bookings. Please try again later.');
        setLoading(false);
      }
    };

    fetchBookings();
  }, []);

  // Calculate total revenue for confirmed bookings
  const totalRevenue = Math.round(bookings.reduce((sum, booking) => sum + booking.totalPrice, 0));

  // Calculate average booking
  const averageBooking = bookings.length > 0 ? Math.round(totalRevenue / bookings.length) : 0;

  // Calculate expenses (65% of total revenue)
  const expenses = Math.round(totalRevenue * 0.65);

  // Calculate profit (total revenue - expenses)
  const profit = Math.round(totalRevenue - expenses); 

  // Prepare data for monthly revenue, expenses, and profit chart for 2025
  const monthlyRevenue = Array(12).fill(0); 
  const monthlyExpenses = Array(12).fill(0);
  const monthlyProfit = Array(12).fill(0);

  bookings.forEach(booking => {
    if (booking.checkIn instanceof Date && !isNaN(booking.checkIn)) {
      const checkInYear = booking.checkIn.getFullYear();
      if (checkInYear === 2025) {
        const month = booking.checkIn.getMonth(); 
        const bookingRevenue = booking.totalPrice || 0;
        monthlyRevenue[month] += bookingRevenue;
        monthlyExpenses[month] += bookingRevenue * 0.65;
        monthlyProfit[month] += bookingRevenue * 0.35; 
      }
    }
  });

  // Round monthly values
  const roundedMonthlyRevenue = monthlyRevenue.map(value => Math.round(value));
  const roundedMonthlyExpenses = monthlyExpenses.map(value => Math.round(value));
  const roundedMonthlyProfit = monthlyProfit.map(value => Math.round(value));

  console.log('Monthly Revenue:', roundedMonthlyRevenue); 
  console.log('Monthly Expenses:', roundedMonthlyExpenses); 
  console.log('Monthly Profit:', roundedMonthlyProfit); 

  const barData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Revenue (₹)',
        data: roundedMonthlyRevenue,
        backgroundColor: '#3b82f6', 
        borderColor: '#2563eb',
        borderWidth: 1,
      },
      {
        label: 'Expenses (₹)',
        data: roundedMonthlyExpenses,
        backgroundColor: '#ef4444', 
        borderColor: '#dc2626',
        borderWidth: 1,
      },
      {
        label: 'Profit (₹)',
        data: roundedMonthlyProfit,
        backgroundColor: '#10b981', 
        borderColor: '#059669',
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Amount (₹)',
        },
        ticks: {
          callback: value => `₹${value.toLocaleString('en-IN')}`, 
        },
      },
      x: {
        title: {
          display: true,
          text: 'Month',
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
    },
  };

  if (loading) {
    return (
      <div className="spinner-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (bookings.length === 0) {
    return <div className="no-data">No confirmed bookings found for 2025.</div>;
  }

  return (
    <div className="dashboard">
      <h2>Finance Analytics</h2>
      <div className="stats-cards">
        <div className="card-dash">
          <h6>Total Revenue</h6>
          <p>₹{totalRevenue.toLocaleString('en-IN')}</p>
        </div>
        <div className="card-dash">
          <h6>Average Booking</h6>
          <p>₹{averageBooking.toLocaleString('en-IN')}</p>
        </div>
        <div className="card-dash">
          <h6>Expenses</h6>
          <p>₹{expenses.toLocaleString('en-IN')}</p>
        </div>
        <div className="card-dash">
          <h6>Profit</h6>
          <p>₹{profit.toLocaleString('en-IN')}</p>
        </div>
      </div>
      <div className="charts-section">
        <div className="chart-card-dash" style={{ height: '400px' }}>
          <h6>Financial Overview (2025)</h6>
          <Bar data={barData} options={chartOptions} />
        </div>
      </div>
    </div>
  );
};

export default FinanceAnalytics;